import { AccessPermitsList } from "components/shared/accessPermitsList";
import BookIndex from "components/shared/book/list";
import BookItemStatus from "components/shared/book/statusCell";
import InlineBtn from "components/shared/inlineBtn";
import SharedReactTable from "components/shared/reactTable";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import {
    accessPermitRelationTypesEnum,
    featureModuleTypesEnum,
    mobileOsTypes,
    organizationFlags,
    organizationFlagsEnum,
} from "helpers/enums";
import { hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import {
    BooleanColumnFilter,
    EnumColumnFilter,
    isDeletedColumn,
    rowIndexColumn,
    SelectColumnFilter,
} from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { enumLabel, flagVal } from "helpers/system";
import policy from "policies";
import Tooltip from "rc-tooltip";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { selectList as accessPermitsList, getInfoList } from "sources/books/accessPermits";
import { sendSms, setResourceActivity } from "sources/books/drivers";
import { list as transportColumnsList } from "sources/books/transportColumns";
import MessengerSettings from "./messengerSettings";

const accessToDriverPasswordsFlagValue = flagVal(
    organizationFlags,
    organizationFlagsEnum.AdminHasAccessToDriverPasswords,
);
const canDownloadFlagAccessFlagValue = flagVal(organizationFlags, organizationFlagsEnum.AdminCanDownloadDriverList);

const HiddenPassword = ({ value }) => {
    const [hover, setHover] = useState(false);

    if (!value) {
        return null;
    }

    const onHover = () => {
        setHover(true);
    };
    const onLeave = () => {
        setHover(false);
    };
    return (
        <div onMouseEnter={onHover} onMouseLeave={onLeave}>
            {hover ? value : value.replace(/./g, "*")}
        </div>
    );
};

export class DriversList extends BookIndex {
    state = { tableColumns: this.tableColumns, accessLoaded: false, accessPermits: [] };

    get tableColumns() {
        const isAdmin = policy("user", { user: { roles: this.props.roles } }).isTruckingOrganizationAdmin();
        const accessToDriverPasswords =
            (accessToDriverPasswordsFlagValue & this.props.organization.intFlags) === accessToDriverPasswordsFlagValue;
        return [
            rowIndexColumn,
            { Header: "ID", accessor: "id", width: 100, filterable: true, filterType: "int" },
            {
                Header: "Имя",
                accessor: "user.fullName",
                id: "user/fullName",
                filterable: true,
                Cell: ({ value, row: { original } }) => {
                    const { id, lastSignInAt, isOnline } = original;
                    return (
                        <span>
                            {this.onlineInfo(isOnline, lastSignInAt, id)}
                            &nbsp;
                            <Link to={paths.driversShow(id)}>{value}</Link>
                        </span>
                    );
                },
            },
            {
                Header: "Номер телефона",
                accessor: "user.phoneNumber",
                width: 150,
                sortable: true,
                id: "user/phoneNumber",
                filterable: true,
                Cell: ({ value }) => hiddenUserData(value, { type: "phone" }),
            },
            {
                Header: "СНИЛС",
                accessor: "snils",
                width: 150,
                filterable: true,
                Cell: ({ value }) =>
                    hiddenUserData(value, { type: "docNumber", valueMask: "xxx-xxx-xxx xx" }),
            },
            {
                Header: "Установлено приложение",
                accessor: "lastSignInAt",
                filterable: true,
                filterType: "nullableField",
                Filter: (props) => (
                    <BooleanColumnFilter
                        {...props}
                        options={[
                            { label: "Показать все", value: "all" },
                            { label: "Установлено", value: "true" },
                            { label: "Не установлено", value: "null" },
                        ]}
                    />
                ),
                Cell: ({ value }) => (value ? "Да" : "Нет"),
                CSVExport: ({ value }) => (value ? "Да" : "Нет"),
            },
            {
                Header: "ОС",
                accessor: "user.deviceInfo.osType",
                id: "user/deviceInfo/osType",
                filterable: true,
                filterType: "enum",
                Filter: (props) => <EnumColumnFilter {...props} filterEnum={mobileOsTypes} />,
                Cell: ({ value }) => enumLabel(mobileOsTypes, value),
            },
            {
                Header: "Статус",
                accessor: "isActive",
                sortable: true,
                filterable: true,
                filterType: "boolean",
                Filter: (props) => (
                    <BooleanColumnFilter
                        {...props}
                        options={[
                            { label: "Показать все", value: "all" },
                            { label: "Активные", value: "true" },
                            { label: "Неактивные", value: "false" },
                        ]}
                    />
                ),
                Cell: ({ value, row: { original } }) => (
                    <BookItemStatus
                        callback={() => this.props.mergeParams({})}
                        modal={this.props.modal}
                        title={`Изменения статуса водителя ${original.user.fullName}`}
                        initialData={{ driverId: original.id }}
                        isActive={value}
                        resourceInactivityInfo={original.resourceInactivityInfo}
                        setResourceActivity={setResourceActivity}
                        canChangeStatus={!policy("user", { user: { roles: this.props.roles } }).isOnlyExtendedViewer()}
                    />
                ),
            },
            {
                Header: "Допуски",
                accessor: "accessPermitIds",
                sortable: false,
                filterable: true,
                filterType: "arrayContains",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            isMulti: true,
                            url: (props) =>
                                accessPermitsList({
                                    filter: { relationType: accessPermitRelationTypesEnum.Driver },
                                    ...props,
                                }),
                            inputProps: { name: "accessPermitIds" },
                            propName: "name",
                            components: { ValueContainer },
                            ...defaultMultipleSelectProps,
                        }}
                    />
                ),
                Cell: ({ value }) => <AccessPermitsList ids={value} items={this.state.accessPermits} />,
            },
            {
                Header: "Колонна",
                accessor: "transportColumn",
                id: "transportColumnId",
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: transportColumnsList,
                            inputProps: { name: "transportColumnId" },
                            propName: "name",
                        }}
                    />
                ),
                Cell: ({ value }) => (value ? value.name : ""),
                CSVExport: ({ value }) => (value ? value.name : ""),
            },
            {
                Header: "Пароль",
                accessor: "password",
                hidden: !(isAdmin && accessToDriverPasswords),
                width: 150,
                sortable: false,
                filterable: false,
                Cell: HiddenPassword,
            },
            {
                Header: "Статус отправки смс",
                accessor: "isSmsSent",
                width: 300,
                filterable: false,
                Cell: ({ value, row: { original } }) =>
                    value ? (
                        <>
                            <span className="pr-3">SMS отправлено</span>
                            {!policy("user", { user: { roles: this.props.roles } }).isReadOnlyTruckingOrganizationUser() &&
                                !this.props.roles.isTruckingOrganizationDispatcher && (
                                    <span
                                        className="inline-btn inline-btn--without-icon"
                                        onClick={this.sendSms.bind(this, original)}
                                    >
                                        Отправить SMS заново
                                    </span>
                                )}
                        </>
                    ) : (
                        !policy("user", { user: { roles: this.props.roles } }).isReadOnlyTruckingOrganizationUser() &&
                        !this.props.roles.isTruckingOrganizationDispatcher && (
                            <span
                                className="inline-btn inline-btn--without-icon"
                                onClick={this.sendSms.bind(this, original)}
                            >
                                Отправить SMS
                            </span>
                        )
                    ),
                CSVExport: ({ value }) => (value ? "SMS отправлено" : "SMS не отправлено"),
            },
            {
                Header: "Отправка сообщений в мессенджер",
                accessor: "isMessengerActive",
                hidden: !this.props.profile.availableFeatureModules.includes(featureModuleTypesEnum.Messenger),
                filterable: true,
                filterType: "boolean",
                Filter: (props) => {
                    const options = [
                        { label: "Показать все", value: "all" },
                        { label: "Включено", value: "true" },
                        { label: "Выключено", value: "false" },
                    ];
                    return <BooleanColumnFilter {...props} options={options} />;
                },
                Cell: ({
                    value,
                    row: {
                        original: { user },
                    },
                }) => (
                    <div className="text-center">
                        {value ? "Да" : "Нет"}
                        <span className="btn btn-xs btn-brand ml-2" onClick={() => this.editMessengers(user.id)}>
                            Настроить
                        </span>
                    </div>
                ),
                CSVExport: ({ value }) => (value ? "Да" : "Нет"),
            },
            isDeletedColumn(this.restore, this.props.canEditBooks),
        ].filter((column) => !column.hidden);
    }

    componentDidMount() {
        getInfoList({ preparedFilter: { relationType: accessPermitRelationTypesEnum.Driver } }).then((res) =>
            this.setState({ accessPermits: res?.data, accessLoaded: true }),
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.canEditBooks !== this.props.canEditBooks || prevState.accessLoaded !== this.state.accessLoaded) {
            this.setState({ tableColumns: this.tableColumns });
        }
    }

    editMessengers = (id) => {
        this.props.modal({
            name: "default",
            title: "Настройка сообщений в мессенджере",
            windowMod: "sm",
            CustomBody: () => <MessengerSettings id={id} />,
        });
    };

    renderHead = () => {
        const { canCreateBooks } = this.props;
        return (
            <div className="book-list__head">
                <div className="book-list__head-close close close--sm" onClick={this.close} />
                <div className="book-list__head-title">
                    {this.props.title}
                    {this.renderCounter()}
                </div>
                <div className="btn-toolbar">{canCreateBooks && this.renderCreateButton()}</div>
            </div>
        );
    };

    sendSms = ({ id, user }) => {
        const { modal } = this.props;
        return modal({
            name: "confirm",
            title: "Отправка SMS",
            text: `Вы уверены, что хотите отправить SMS на номер "${user.phoneNumber}"?`,
            buttons: {
                confirm: "Отправить SMS",
            },
            action: () => {
                sendSms({ id, userId: user.id, phoneNumber: user.phoneNumber }).then(() => this.props.mergeParams());
            },
        });
    };

    renderListComponent = () => {
        const accessToDownload =
            (canDownloadFlagAccessFlagValue & this.props.organization.intFlags) === canDownloadFlagAccessFlagValue;
        const extraProps =
            accessToDownload && this.props.roles.isTruckingOrganizationAdmin
                ? { pageSizeOptions: [30, 50, 100, 1000] }
                : { isNotExportable: true };
        return (
            <SharedReactTable
                {...extraProps}
                {...this.props}
                columns={this.state.tableColumns}
                filtered={{ isDeleted: { filterType: "boolean", id: "isDeleted", value: "false" } }}
            />
        );
    };

    renderCreateButton = () => {
        const { modal, mergeParams } = this.props;
        const action = () =>
            modal({
                name: "createDriver",
                title: "Создание водителя",
                windowMod: "wide",
                mergeParams,
            });
        return (
            <a className="btn btn-brand btn-sm" onClick={action}>
                Создать водителя
            </a>
        );
    };

    renderActions = () => {
        const { mergeParams, organization } = this.props;
        const actions = [{ value: "Обновить", cname: "refresh", active: true, action: mergeParams, params: {} }];
        return (
            <div className="d-flex justify-content-between">
                {actions.map((item, index) => (
                    <InlineBtn key={index} {...item} />
                ))}
                <small>
                    Пин-код: <b>{organization.pinCode}</b>
                </small>
            </div>
        );
    };

    onlineInfo = (isOnline, lastSignInAt) => {
        if (lastSignInAt) {
            if (isOnline) {
                return (
                    <Tooltip placement="top" overlay="Онлайн">
                        <i className="icon-uEA70-circle driver-status__icon text-success" />
                    </Tooltip>
                );
            } else {
                const { organizationTimezone } = this.props;
                if (!organizationTimezone.id) {
                    return null;
                }
                const localeLastSignInAt = new Date(lastSignInAt).toLocaleString("ru-RU", {
                    timeZone: organizationTimezone.id,
                });
                return (
                    <Tooltip
                        placement="top"
                        overlay={`Был онлайн: ${localeLastSignInAt} ${organizationTimezone.displayName}`}
                    >
                        <i className="icon-uEA70-circle driver-status__icon text-muted" />
                    </Tooltip>
                );
            }
        } else {
            return (
                <Tooltip placement="top" overlay="Моб. приложение не установлено">
                    <i className="icon-uEA70-circle driver-status__icon text-danger" />
                </Tooltip>
            );
        }
    };
}

export default tableContainer(DriversList, "drivers");
